import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, LinearProgress, ToggleButtonGroup,
  ToggleButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, Tabs, Tab, useTheme,
} from '@mui/material';
import { Assessment as ReportsIcon, Groups as CrossExecIcon, FolderOpen as ProjectsIcon } from '@mui/icons-material';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { workItemsApi, driversApi, reportingApi, projectsApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import type { WorkItem, AnnualStrategicDriver, WorkItemType, Priority, Project } from '../types/models';

const TYPE_COLORS: Record<string, string> = {
  Task: '#6C9BFF',
  Decision: '#FF8A65',
  Briefing: '#69F0AE',
  Review: '#FFB74D',
  FollowUp: '#CE93D8',
  Draft: '#4DD0E1',
  Reading: '#AED581',
};

type DateRange = 'week' | 'month' | 'lastMonth';

function useChartColors() {
  const theme = useTheme();
  return useMemo(() => ({
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    textPrimary: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    bgPaper: theme.palette.background.paper,
    divider: theme.palette.divider,
    tooltipBg: theme.palette.background.paper,
    gridLine: theme.palette.divider,
  }), [theme]);
}

function usePriorityColors() {
  const C = useChartColors();
  return useMemo(() => ({
    Critical: C.error,
    High: C.secondary,
    Medium: C.primary,
    Low: C.success,
  }), [C]);
}

function CustomTooltip({ active, payload, label }: any) {
  const C = useChartColors();
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      backgroundColor: C.tooltipBg,
      border: '1px solid',
      borderColor: C.divider,
      borderRadius: 2,
      color: C.textPrimary,
      fontSize: 13,
      p: '8px 12px',
    }}>
      {label && <Typography variant="caption" sx={{ color: C.textSecondary, display: 'block', mb: 0.5 }}>{label}</Typography>}
      {payload.map((entry: any, i: number) => (
        <Typography key={i} variant="body2" sx={{ color: entry.color || C.textPrimary }}>
          {entry.name}: {entry.value}{typeof entry.value === 'number' && entry.name?.includes('Min') ? 'm' : ''}
        </Typography>
      ))}
    </Box>
  );
}

function getDateRange(range: DateRange): { start: Date; end: Date } {
  const now = new Date();
  if (range === 'week') {
    const day = now.getDay();
    const diff = day === 0 ? 6 : day - 1; // Monday start
    const start = new Date(now);
    start.setDate(now.getDate() - diff);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (range === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  }
  // lastMonth
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  return { start, end };
}

function makeRenderCustomLabel(textColor: string) {
  return ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill={textColor} textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };
}

export default function ReportsPage() {
  const { user } = useAuth();
  const C = useChartColors();
  const PRIORITY_COLORS = usePriorityColors();
  const renderCustomLabel = useMemo(() => makeRenderCustomLabel(C.textPrimary), [C.textPrimary]);
  const [items, setItems] = useState<WorkItem[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<DateRange>('month');
  const [activeTab, setActiveTab] = useState(0);
  const [capacityForecast, setCapacityForecast] = useState<any>(null);
  const canSeeCrossExec = user?.role === 'Admin' || user?.role === 'TenantAdmin' || user?.role === 'ExecutiveAssistant';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [itemsData, driversData] = await Promise.all([
          workItemsApi.getAll(),
          driversApi.getActive(),
        ]);
        setItems(itemsData);
        setDrivers(driversData);
      } catch (err) {
        console.error('Failed to load report data', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    // Load capacity forecast separately (non-blocking)
    reportingApi.getCapacityForecast(4).then(setCapacityForecast).catch(() => {});
  }, []);

  // Filter items by date range
  const filteredItems = useMemo(() => {
    const { start, end } = getDateRange(range);
    return items.filter(item => {
      const d = new Date(item.targetDate);
      return d >= start && d <= end;
    });
  }, [items, range]);

  // ---- Computed metrics ----

  // Time Allocation
  const timeAllocation = useMemo(() => {
    const focusMinutes = filteredItems
      .filter(i => i.type === 'Task' || i.type === 'Draft' || i.type === 'Reading')
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    const meetingMinutes = filteredItems
      .filter(i => i.type === 'Briefing' || i.type === 'Review')
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    const plannedMinutes = filteredItems
      .filter(i => i.status === 'Planned' || i.status === 'InProgress')
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    const overflowMinutes = filteredItems
      .filter(i => i.status === 'Deferred' || i.isCarryForward)
      .reduce((s, i) => s + i.estimatedMinutes, 0);

    return [
      { name: 'Focus Time', value: focusMinutes, color: C.primary },
      { name: 'Meetings', value: meetingMinutes, color: C.secondary },
      { name: 'Planned Work', value: plannedMinutes, color: C.success },
      { name: 'Overflow', value: overflowMinutes, color: C.error },
    ].filter(d => d.value > 0);
  }, [filteredItems, C]);

  // Work Item Breakdown by Type
  const typeBreakdown = useMemo(() => {
    const allTypes: WorkItemType[] = ['Task', 'Decision', 'Briefing', 'Review', 'FollowUp', 'Draft', 'Reading'];
    return allTypes.map(type => {
      const ofType = filteredItems.filter(i => i.type === type);
      return {
        name: type,
        count: ofType.length,
        minutes: ofType.reduce((s, i) => s + i.estimatedMinutes, 0),
        color: TYPE_COLORS[type],
      };
    }).filter(d => d.count > 0);
  }, [filteredItems]);

  // Priority Distribution
  const priorityDistribution = useMemo(() => {
    const allPriorities: Priority[] = ['Critical', 'High', 'Medium', 'Low'];
    return allPriorities.map(p => ({
      name: p,
      value: filteredItems.filter(i => i.priority === p).length,
      color: PRIORITY_COLORS[p],
    })).filter(d => d.value > 0);
  }, [filteredItems, PRIORITY_COLORS]);

  // Time Hogs — top 10 by estimated minutes
  const timeHogs = useMemo(() => {
    return [...filteredItems]
      .sort((a, b) => b.estimatedMinutes - a.estimatedMinutes)
      .slice(0, 10);
  }, [filteredItems]);

  // Strategic Alignment
  const strategicAlignment = useMemo(() => {
    const linked = filteredItems.filter(i => i.strategicDriverId).length;
    const unlinked = filteredItems.length - linked;
    return [
      { name: 'Linked to Strategy', value: linked, color: C.primary },
      { name: 'Unlinked', value: unlinked, color: C.textSecondary },
    ].filter(d => d.value > 0);
  }, [filteredItems, C]);

  // Carry-Forward Frequency
  const carryForwardData = useMemo(() => {
    const cfItems = filteredItems.filter(i => i.isCarryForward);
    // Group by title
    const counts: Record<string, { title: string; count: number; minutes: number }> = {};
    cfItems.forEach(item => {
      const key = item.title;
      if (!counts[key]) counts[key] = { title: key, count: 0, minutes: item.estimatedMinutes };
      counts[key].count += 1;
    });
    return Object.values(counts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredItems]);

  if (!user) return null;

  const cardSx = {
    height: '100%',
    bgcolor: 'background.paper',
  };

  const sectionTitle = (text: string) => (
    <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>{text}</Typography>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReportsIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h4">Reports</Typography>
        </Box>
        {activeTab === 0 && (
          <ToggleButtonGroup
            value={range}
            exclusive
            onChange={(_, v) => { if (v) setRange(v); }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: 'text.secondary',
                borderColor: 'divider',
                px: 2,
                py: 0.75,
                fontSize: '0.8rem',
                fontWeight: 600,
                '&.Mui-selected': {
                  bgcolor: (t: any) => `${t.palette.primary.main}26`,
                  color: 'primary.main',
                  borderColor: (t: any) => `${t.palette.primary.main}4D`,
                },
              },
            }}
          >
            <ToggleButton value="week">This Week</ToggleButton>
            <ToggleButton value="month">This Month</ToggleButton>
            <ToggleButton value="lastMonth">Last Month</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
        }}
      >
        <Tab label="My Reports" />
        <Tab label="Projects" icon={<ProjectsIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        {canSeeCrossExec && <Tab label="Cross-Executive" icon={<CrossExecIcon sx={{ fontSize: 18 }} />} iconPosition="start" />}
      </Tabs>

      {/* My Reports Tab */}
      {activeTab === 0 && (
        <>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && filteredItems.length === 0 && (
        <Card sx={cardSx}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">No work items found for this period</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Adjust the date range or create work items to see analytics.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && filteredItems.length > 0 && (
        <Grid container spacing={2.5}>
          {/* Time Allocation Pie */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Time Allocation')}
                {timeAllocation.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={timeAllocation}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={110}
                        paddingAngle={3}
                        dataKey="value"
                        labelLine={false}
                        label={renderCustomLabel}
                      >
                        {timeAllocation.map((entry, index) => (
                          <Cell key={index} fill={entry.color} stroke="transparent" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>No time data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Priority Distribution Pie */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Priority Distribution')}
                {priorityDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={priorityDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={110}
                        paddingAngle={3}
                        dataKey="value"
                        labelLine={false}
                        label={renderCustomLabel}
                      >
                        {priorityDistribution.map((entry, index) => (
                          <Cell key={index} fill={entry.color} stroke="transparent" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>No priority data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Work Item Breakdown Bar Chart */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Work Item Breakdown')}
                {typeBreakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={typeBreakdown} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.gridLine} horizontal={false} />
                      <XAxis type="number" tick={{ fill: C.textSecondary, fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fill: C.textSecondary, fontSize: 12 }} axisLine={false} tickLine={false} width={70} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                      <Bar dataKey="count" name="Count" radius={[0, 4, 4, 0]} barSize={16}>
                        {typeBreakdown.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                      <Bar dataKey="minutes" name="Est. Minutes" radius={[0, 4, 4, 0]} barSize={16} fillOpacity={0.5}>
                        {typeBreakdown.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>No type data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Strategic Alignment Pie */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Strategic Alignment')}
                {strategicAlignment.length > 0 ? (
                  <Box>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={strategicAlignment}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={85}
                          paddingAngle={3}
                          dataKey="value"
                          labelLine={false}
                          label={renderCustomLabel}
                        >
                          {strategicAlignment.map((entry, index) => (
                            <Cell key={index} fill={entry.color} stroke="transparent" />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {drivers.length > 0 && (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {drivers.map(d => (
                          <Box key={d.id}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: C.textSecondary }}>{d.title}</Typography>
                              <Typography variant="caption" sx={{ color: C.primary, fontWeight: 600 }}>{d.progressPercentage}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={d.progressPercentage} sx={{ height: 3, borderRadius: 2 }} />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>No data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Time Hogs Table */}
          <Grid size={{ xs: 12 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Time Hogs')}
                <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
                  Top 10 most time-consuming work items
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Priority</TableCell>
                        <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Est. Minutes</TableCell>
                        <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timeHogs.map(item => {
                        const isHigh = item.estimatedMinutes > 90;
                        return (
                          <TableRow
                            key={item.id}
                            sx={{
                              bgcolor: isHigh ? 'rgba(255, 82, 82, 0.06)' : 'transparent',
                              '& td': { borderColor: C.gridLine },
                            }}
                          >
                            <TableCell sx={{ color: C.textPrimary, fontWeight: isHigh ? 600 : 400 }}>
                              {item.title}
                              {isHigh && (
                                <Chip label="90m+" size="small" sx={{ ml: 1, bgcolor: 'rgba(255,82,82,0.15)', color: C.error, fontSize: '0.65rem', height: 20 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.type}
                                size="small"
                                sx={{ bgcolor: `${TYPE_COLORS[item.type]}20`, color: TYPE_COLORS[item.type], fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.priority}
                                size="small"
                                sx={{ bgcolor: `${PRIORITY_COLORS[item.priority]}20`, color: PRIORITY_COLORS[item.priority], fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: isHigh ? C.error : C.textPrimary, fontWeight: 600 }}>
                              {item.estimatedMinutes}m
                            </TableCell>
                            <TableCell>
                              <Chip label={item.status} size="small" variant="outlined" sx={{ borderColor: 'divider', color: C.textSecondary }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {timeHogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', color: C.textSecondary, py: 4 }}>
                            No items found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Carry-Forward Frequency */}
          <Grid size={{ xs: 12 }}>
            <Card sx={cardSx}>
              <CardContent>
                {sectionTitle('Carry-Forward Frequency')}
                <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
                  Items that keep getting pushed — potential blockers requiring attention
                </Typography>
                {carryForwardData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(200, carryForwardData.length * 40 + 40)}>
                    <BarChart data={carryForwardData} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.gridLine} horizontal={false} />
                      <XAxis type="number" tick={{ fill: C.textSecondary, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="title"
                        tick={{ fill: C.textSecondary, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={180}
                        tickFormatter={(value: string) => value.length > 28 ? value.substring(0, 25) + '...' : value}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                      <Bar dataKey="count" name="Times Carried Forward" fill={C.warning} radius={[0, 4, 4, 0]} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No carry-forward items in this period</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Capacity Forecast */}
          {capacityForecast && (capacityForecast?.weeks || (Array.isArray(capacityForecast) ? capacityForecast : [])).length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Card sx={cardSx}>
                <CardContent>
                  {sectionTitle('Capacity Forecast')}
                  <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
                    Planned vs available minutes for the next 4 weeks
                  </Typography>
                  <Stack spacing={2}>
                    {(capacityForecast?.weeks || (Array.isArray(capacityForecast) ? capacityForecast : [])).map((week: any, i: number) => {
                      const utilisation = week.utilisationPercent ?? (week.availableMinutes > 0 ? Math.round((week.plannedMinutes / week.availableMinutes) * 100) : 0);
                      const isOverloaded = week.isOverloaded ?? utilisation > 100;
                      return (
                        <Box key={week.weekStart || i}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: C.textSecondary }}>
                              {week.weekStart || week.weekLabel} — {week.weekEnd || ''}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isOverloaded ? C.error : C.textPrimary, fontWeight: 600 }}>
                              {utilisation}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, utilisation)}
                            color={isOverloaded ? 'error' : 'primary'}
                            sx={{ height: 8, borderRadius: 4, mb: 0.5, bgcolor: 'rgba(255,255,255,0.06)' }}
                          />
                          <Typography variant="caption" sx={{ color: isOverloaded ? C.error : C.textSecondary }}>
                            {week.plannedMinutes}min planned / {week.availableMinutes}min available
                            {isOverloaded && ' — Overloaded'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
        </>
      )}

      {/* Projects Tab */}
      {activeTab === 1 && (
        <ProjectsTab drivers={drivers} workItems={items} />
      )}

      {/* Cross-Executive Tab */}
      {activeTab === 2 && canSeeCrossExec && (
        <CrossExecutiveTab />
      )}
    </Box>
  );
}

// ---- Projects Report Tab ----

function ProjectsTab({ drivers, workItems }: { drivers: AnnualStrategicDriver[]; workItems: WorkItem[] }) {
  const C = useChartColors();
  const theme = useTheme();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await projectsApi.getPortfolio();
        setProjects(data);
      } catch (err) {
        console.error('Failed to load projects', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // RAG counts
  const ragCounts = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    return {
      Green: active.filter(p => p.ragStatus === 'Green').length,
      Amber: active.filter(p => p.ragStatus === 'Amber').length,
      Red: active.filter(p => p.ragStatus === 'Red').length,
    };
  }, [projects]);

  // Time allocation by project (from work items linked to projects)
  const timeByProject = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    const projectTimes = active.map(project => {
      const linkedItems = workItems.filter(wi => wi.projectId === project.id);
      const totalMinutes = linkedItems.reduce((sum, wi) => sum + wi.estimatedMinutes, 0);
      return { id: project.id, name: project.title, totalMinutes, totalHours: +(totalMinutes / 60).toFixed(1) };
    }).filter(p => p.totalMinutes > 0).sort((a, b) => b.totalMinutes - a.totalMinutes);

    const grandTotal = projectTimes.reduce((s, p) => s + p.totalMinutes, 0);
    return projectTimes.map(p => ({
      ...p,
      percentage: grandTotal > 0 ? Math.round((p.totalMinutes / grandTotal) * 100) : 0,
    }));
  }, [projects, workItems]);

  // Strategic alignment: drivers with/without projects
  const alignment = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    const driverIdsWithProjects = new Set(active.map(p => p.strategicDriverId).filter(Boolean));
    const withProjects = drivers.filter(d => driverIdsWithProjects.has(d.id));
    const withoutProjects = drivers.filter(d => !driverIdsWithProjects.has(d.id));
    return { withProjects, withoutProjects };
  }, [projects, drivers]);

  // Stale projects: no work item updates and no indicator updates in last 14 days
  const staleProjects = useMemo(() => {
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');

    return active.map(project => {
      const linkedItems = workItems.filter(wi => wi.projectId === project.id);
      const latestWorkItemUpdate = linkedItems.length > 0
        ? new Date(Math.max(...linkedItems.map(wi => new Date(wi.updatedAt).getTime())))
        : null;
      const projectUpdate = new Date(project.updatedAt);
      const lastActivity = latestWorkItemUpdate && latestWorkItemUpdate > projectUpdate
        ? latestWorkItemUpdate
        : projectUpdate;
      const daysSinceActivity = Math.floor((Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));
      return { ...project, lastActivity, daysSinceActivity };
    }).filter(p => p.daysSinceActivity >= 14).sort((a, b) => b.daysSinceActivity - a.daysSinceActivity);
  }, [projects, workItems]);

  const ragColor = (rag: string) => {
    if (rag === 'Green') return theme.palette.success.main;
    if (rag === 'Amber') return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && projects.length === 0 && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ProjectsIcon sx={{ fontSize: 48, color: C.textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">No projects found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create projects to see portfolio reporting here.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && projects.length > 0 && (
        <Grid container spacing={2.5}>
          {/* Portfolio RAG Summary */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>Portfolio RAG Summary</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ bgcolor: 'background.paper' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: theme.palette.success.main, fontWeight: 700 }}>{ragCounts.Green}</Typography>
                    <Typography variant="body2" sx={{ color: C.textSecondary, fontWeight: 600, mt: 0.5 }}>Green</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ bgcolor: 'background.paper' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: theme.palette.warning.main, fontWeight: 700 }}>{ragCounts.Amber}</Typography>
                    <Typography variant="body2" sx={{ color: C.textSecondary, fontWeight: 600, mt: 0.5 }}>Amber</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ bgcolor: 'background.paper' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: theme.palette.error.main, fontWeight: 700 }}>{ragCounts.Red}</Typography>
                    <Typography variant="body2" sx={{ color: C.textSecondary, fontWeight: 600, mt: 0.5 }}>Red</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Time Allocation by Project */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>Time Allocation by Project</Typography>
                {timeByProject.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Project</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Hours</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600, width: '40%' }}>Allocation</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {timeByProject.map(p => (
                          <TableRow key={p.id} sx={{ '& td': { borderColor: C.gridLine } }}>
                            <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>{p.name}</TableCell>
                            <TableCell align="right" sx={{ color: C.textPrimary, fontWeight: 600 }}>{p.totalHours}h</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={p.percentage}
                                  sx={{
                                    flex: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)',
                                    '& .MuiLinearProgress-bar': { borderRadius: 4 },
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: C.textSecondary, minWidth: 32, fontWeight: 600 }}>
                                  {p.percentage}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No time tracked against projects yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Strategic Alignment */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>Strategic Alignment</Typography>
                {drivers.length > 0 ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ color: C.success, fontWeight: 600, mb: 1 }}>
                        Drivers with Projects ({alignment.withProjects.length})
                      </Typography>
                      {alignment.withProjects.length > 0 ? (
                        <Stack spacing={1}>
                          {alignment.withProjects.map(d => (
                            <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.success, flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ color: C.textPrimary }}>{d.title}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: C.textSecondary }}>None</Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ color: C.warning, fontWeight: 600, mb: 1 }}>
                        Drivers without Projects ({alignment.withoutProjects.length})
                      </Typography>
                      {alignment.withoutProjects.length > 0 ? (
                        <Stack spacing={1}>
                          {alignment.withoutProjects.map(d => (
                            <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.warning, flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ color: C.textPrimary }}>{d.title}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: C.textSecondary }}>None</Typography>
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No strategic drivers configured
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Stale Project Detection */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1, color: C.textPrimary, fontWeight: 600 }}>Stale Project Detection</Typography>
                <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
                  Projects with no activity in the last 14 days
                </Typography>
                {staleProjects.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Project</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>RAG</TableCell>
                          <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Last Activity</TableCell>
                          <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Days Stale</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {staleProjects.map(p => (
                          <TableRow key={p.id} sx={{ '& td': { borderColor: C.gridLine } }}>
                            <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>{p.title}</TableCell>
                            <TableCell>
                              <Chip
                                label={p.ragStatus}
                                size="small"
                                sx={{ bgcolor: `${ragColor(p.ragStatus)}20`, color: ragColor(p.ragStatus), fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: C.textSecondary }}>
                              {p.lastActivity.toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{ color: p.daysSinceActivity >= 30 ? C.error : C.warning, fontWeight: 600 }}
                              >
                                {p.daysSinceActivity}d
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">All projects have recent activity</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ---- Cross-Executive Report Tab ----

function CrossExecutiveTab() {
  const C = useChartColors();
  const [crossData, setCrossData] = useState<any>(null);
  const [forecast, setForecast] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('month');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { start, end } = getDateRange(dateRange);
        const from = start.toISOString().split('T')[0];
        const to = end.toISOString().split('T')[0];
        const [crossResult, forecastResult] = await Promise.allSettled([
          reportingApi.getCrossExecutiveReport(from, to),
          reportingApi.getCapacityForecast(4),
        ]);
        if (crossResult.status === 'fulfilled') setCrossData(crossResult.value);
        if (forecastResult.status === 'fulfilled') setForecast(forecastResult.value);
        if (crossResult.status === 'rejected' && forecastResult.status === 'rejected') {
          setError('Failed to load cross-executive reports.');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dateRange]);

  const executives: any[] = crossData?.executives || crossData?.reports || (Array.isArray(crossData) ? crossData : []);
  const forecastWeeks: any[] = forecast?.weeks || (Array.isArray(forecast) ? forecast : []);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: C.textPrimary, fontWeight: 600 }}>
          Cross-Executive Overview
        </Typography>
        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(_, v) => { if (v) setDateRange(v); }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              borderColor: 'divider',
              px: 2,
              py: 0.75,
              fontSize: '0.8rem',
              fontWeight: 600,
              '&.Mui-selected': {
                bgcolor: (t: any) => `${t.palette.primary.main}26`,
                color: 'primary.main',
                borderColor: (t: any) => `${t.palette.primary.main}4D`,
              },
            },
          }}
        >
          <ToggleButton value="week">This Week</ToggleButton>
          <ToggleButton value="month">This Month</ToggleButton>
          <ToggleButton value="lastMonth">Last Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Card sx={{ bgcolor: 'background.paper', mb: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">{error}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Executive Comparison Table */}
      {!loading && executives.length > 0 && (
        <Card sx={{ bgcolor: 'background.paper', mb: 2.5 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
              Executive Comparison
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Executive</TableCell>
                    <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Completion Rate</TableCell>
                    <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Focus Time Ratio</TableCell>
                    <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Carry-Forward</TableCell>
                    <TableCell align="right" sx={{ color: C.textSecondary, borderColor: C.gridLine, fontWeight: 600 }}>Strategic Alignment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executives.map((exec: any, i: number) => {
                    const completionRate = exec.completionRate ?? exec.completionPercent ?? 0;
                    const focusRatio = exec.focusTimeRatio ?? exec.focusTimePercent ?? 0;
                    const carryForward = exec.carryForwardCount ?? exec.carryForwards ?? 0;
                    const strategicPct = exec.strategicAlignmentPercent ?? exec.strategicAlignment ?? 0;
                    return (
                      <TableRow key={exec.executiveUserId || exec.executiveName || i} sx={{ '& td': { borderColor: C.gridLine } }}>
                        <TableCell sx={{ color: C.textPrimary, fontWeight: 500 }}>
                          {exec.executiveName || exec.displayName || exec.email || `Executive ${i + 1}`}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(completionRate, 100)}
                              sx={{
                                width: 60, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)',
                                '& .MuiLinearProgress-bar': { bgcolor: completionRate >= 70 ? C.success : completionRate >= 40 ? C.warning : C.error, borderRadius: 3 },
                              }}
                            />
                            <Typography variant="body2" sx={{ color: C.textPrimary, fontWeight: 600, minWidth: 40 }}>
                              {Math.round(completionRate)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: focusRatio >= 30 ? C.success : C.warning, fontWeight: 600 }}>
                            {Math.round(focusRatio)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={carryForward}
                            size="small"
                            sx={{
                              bgcolor: carryForward > 5 ? 'rgba(255,82,82,0.15)' : 'rgba(255,255,255,0.06)',
                              color: carryForward > 5 ? C.error : C.textPrimary,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: strategicPct >= 50 ? C.primary : C.textSecondary, fontWeight: 600 }}>
                            {Math.round(strategicPct)}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {!loading && executives.length === 0 && !error && (
        <Card sx={{ bgcolor: 'background.paper', mb: 2.5 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CrossExecIcon sx={{ fontSize: 48, color: C.textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">No cross-executive data available</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Cross-executive reports require multiple executives with active plans.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Capacity Forecast */}
      {!loading && forecastWeeks.length > 0 && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary, fontWeight: 600 }}>
              Capacity Forecast — Next 4 Weeks
            </Typography>
            <Typography variant="body2" sx={{ color: C.textSecondary, mb: 2 }}>
              Planned minutes vs available minutes per week
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={forecastWeeks} margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.gridLine} />
                <XAxis
                  dataKey="weekLabel"
                  tick={{ fill: C.textSecondary, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: string) => value || ''}
                />
                <YAxis
                  tick={{ fill: C.textSecondary, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: number) => `${Math.round(value / 60)}h`}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <Box sx={{ backgroundColor: C.tooltipBg, border: '1px solid', borderColor: C.divider, borderRadius: 2, color: C.textPrimary, fontSize: 13, p: '8px 12px' }}>
                        <Typography variant="caption" sx={{ color: C.textSecondary, display: 'block', mb: 0.5 }}>{label}</Typography>
                        {payload.map((entry: any, i: number) => (
                          <Typography key={i} variant="body2" sx={{ color: entry.color }}>
                            {entry.name}: {Math.round(entry.value / 60)}h ({entry.value}m)
                          </Typography>
                        ))}
                      </Box>
                    );
                  }}
                  cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                />
                <Legend formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>} />
                <Bar dataKey="availableMinutes" name="Available" fill={C.primary} radius={[4, 4, 0, 0]} barSize={32} fillOpacity={0.4} />
                <Bar dataKey="plannedMinutes" name="Planned" fill={C.secondary} radius={[4, 4, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
